import {
  formatDate,
  type LangueCgu,
  type Langue,
  type TexteCgu,
  type TexteVersionCgu,
} from '@artosera/shared';
import { Button, Checkbox, Tabs } from '@artosera/ui';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { authApi } from '../../api/auth.js';
import { estApiError } from '../../api/errors.js';
import {
  capturerPortee,
  estPorteeObsolete,
  exigerActuelle,
  oublierIdentite,
} from '../../auth/identite.js';
import { queryClient } from '../../auth/queryClient.js';
import { chargerSession } from '../../auth/session.js';
import { useLangue } from '../../composants/useLangue.js';
import { AuthCard } from '../AuthCard.js';
import { allerApresConnexion } from '../connexion/connexionFlow.js';
import type { DonneesConditions } from './conditionsLoader.js';
import styles from './ConditionsPage.module.css';

/** « Version du 4 octobre 2026 » : l'identifiant est la date de mise à jour du texte. */
function dateDeVersion(version: string, langue: Langue): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(version)
    ? formatDate(new Date(`${version}T12:00:00Z`), langue)
    : version;
}

function dateDInstant(instant: string, langue: Langue): string {
  const date = new Date(instant);
  return Number.isNaN(date.getTime()) ? instant : formatDate(date, langue);
}

function raisonDe(erreur: unknown): unknown {
  return estApiError(erreur) ? (erreur.details as { raison?: unknown } | null)?.raison : undefined;
}

/** Le texte, en données — jamais de HTML injecté. */
function TexteCguVue({ texte, langue }: { texte: TexteCgu; langue: LangueCgu }): JSX.Element {
  const { t } = useTranslation('regie');
  return (
    // Région défilante : atteignable au clavier, et nommée.
    <div
      className={styles.texte}
      tabIndex={0}
      role="region"
      aria-label={t('auth.conditions.texte')}
      lang={langue}
    >
      <h2 className={styles.titreTexte}>{texte.titre}</h2>
      {texte.articles.map((article) => (
        <section key={article.numero} className={styles.article}>
          <h3 className={styles.titreArticle}>
            {article.numero}. {article.titre}
          </h3>
          {article.blocs.map((bloc, rang) =>
            bloc.type === 'paragraphe' ? (
              <p key={rang}>{bloc.texte}</p>
            ) : (
              <ul key={rang}>
                {bloc.elements.map((element, position) => (
                  <li key={position}>{element}</li>
                ))}
              </ul>
            ),
          )}
        </section>
      ))}
    </div>
  );
}

/** Les deux langues, toutes deux consultables ; celle affichée est celle acceptée. */
function TexteBilingue({
  texte,
  langue,
  changerLangue,
}: {
  texte: TexteVersionCgu;
  langue: LangueCgu;
  changerLangue: (langue: LangueCgu) => void;
}): JSX.Element {
  const { t } = useTranslation('regie');
  return (
    <div className={styles.bilingue}>
      <Tabs
        value={langue}
        onValueChange={(valeur) => changerLangue(valeur === 'en' ? 'en' : 'fr')}
        tabs={[
          {
            value: 'fr',
            label: <span lang="fr">Français</span>,
            content: <TexteCguVue texte={texte.fr} langue="fr" />,
          },
          {
            value: 'en',
            label: <span lang="en">English</span>,
            content: <TexteCguVue texte={texte.en} langue="en" />,
          },
        ]}
      />
      <p className={styles.note}>{t('auth.conditions.noteLangue')}</p>
    </div>
  );
}

/**
 * `/app/conditions` (ARTO-59, article 2) : accepter les conditions générales
 * d'utilisation quand la session l'exige, les consulter sinon.
 *
 * L'acceptation est une action EXPRESSE : la case n'est jamais pré-cochée, le
 * bouton reste inactif tant qu'elle ne l'est pas, et la langue envoyée est
 * celle du texte affiché au moment du geste. Le texte vient des données de la
 * régie (`cgu/textes/`), scellées par les empreintes du catalogue partagé.
 */
export function ConditionsPage(): JSX.Element {
  const { t } = useTranslation('regie');
  const langueInterface = useLangue();
  const { session, retour, version, texte } = useLoaderData<DonneesConditions>();
  const navigate = useNavigate();
  const revalidateur = useRevalidator();
  const exigence = session.cguAcceptationRequise ?? null;

  const [langue, setLangue] = useState<LangueCgu>(langueInterface);
  // La case vaut pour UNE version : une autre à l'écran, et elle n'est plus
  // cochée — ce qui avait été accepté des yeux ne vaut pas pour la nouvelle.
  const [cocheePour, setCocheePour] = useState<string | null>(null);
  const coche = version !== null && cocheePour === version;
  const [enCours, setEnCours] = useState<'accepter' | 'refuser' | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [perimee, setPerimee] = useState(false);

  async function accepter(): Promise<void> {
    if (!version || !texte) return;
    const avant = capturerPortee();
    setErreur(null);
    setEnCours('accepter');
    try {
      await authApi.cguAccepter({ version, langue });
      exigerActuelle(avant);
      // La session est relue : libre, elle rouvre aussi le hors-ligne. Un échec
      // ici n'empêche rien, le chargeur du retour la relira.
      await chargerSession(null).catch(() => undefined);
      await allerApresConnexion(navigate, retour);
    } catch (error) {
      if (estPorteeObsolete(error)) return;
      if (raisonDe(error) === 'cgu_version_perimee') {
        // Le texte affiché n'est plus celui en vigueur : on recharge la
        // version actuelle, et on le dit.
        setPerimee(true);
        setCocheePour(null);
        void revalidateur.revalidate();
        return;
      }
      setErreur(estApiError(error) ? error.message : t('auth.connexion.erreurReseau'));
    } finally {
      setEnCours(null);
    }
  }

  async function refuser(): Promise<void> {
    setErreur(null);
    setEnCours('refuser');
    try {
      await authApi.cguRefuser();
    } catch (error) {
      setEnCours(null);
      if (raisonDe(error) === 'cgu_refus_sans_objet') {
        // Acceptées entre-temps, depuis une autre session : la page le montre.
        void revalidateur.revalidate();
        return;
      }
      if (!estApiError(error) || error.status !== 401) {
        setErreur(estApiError(error) ? error.message : t('auth.connexion.erreurReseau'));
        return;
      }
    }
    // Comme la déconnexion : l'identité et le cache entier sont oubliés.
    await oublierIdentite(queryClient);
    await navigate('/connexion', { replace: true });
  }

  const titre = t('auth.conditions.titre');

  if (!exigence) {
    // ── Consultation ─────────────────────────────────────────────────────────
    const acceptation = session.cgu ?? null;
    return (
      <AuthCard title={titre} large>
        <div className={styles.page}>
          {acceptation ? (
            <p className={styles.meta}>
              {t('auth.conditions.acceptee', {
                version: dateDeVersion(acceptation.version, langueInterface),
                date: dateDInstant(acceptation.accepteeLe, langueInterface),
                langue: t(`auth.conditions.langues.${acceptation.langue}`),
              })}
            </p>
          ) : (
            <p className={styles.meta}>
              {version
                ? t('auth.conditions.aucuneAcceptation')
                : t('auth.conditions.aucuneVersion')}
            </p>
          )}
          {version && texte ? (
            <TexteBilingue texte={texte} langue={langue} changerLangue={setLangue} />
          ) : version ? (
            <p className={styles.erreur}>{t('auth.conditions.texteIndisponible')}</p>
          ) : null}
          <p className={styles.retour}>
            <Link to={retour}>{t('auth.conditions.retour')}</Link>
          </p>
        </div>
      </AuthCard>
    );
  }

  // ── Acceptation ────────────────────────────────────────────────────────────
  const dateVersion = dateDeVersion(exigence.version, langueInterface);
  return (
    <AuthCard title={titre} subtitle={t('auth.conditions.sousTitre')} large>
      <form
        className={styles.page}
        onSubmit={(event) => {
          event.preventDefault();
          void accepter();
        }}
        noValidate
      >
        <p className={styles.meta}>
          {exigence.enVigueurLe
            ? t('auth.conditions.versionEnVigueur', {
                version: dateVersion,
                date: dateDInstant(exigence.enVigueurLe, langueInterface),
              })
            : t('auth.conditions.version', { version: dateVersion })}
        </p>
        {perimee ? (
          <p className={styles.statut} role="status">
            {t('auth.conditions.perimee')}
          </p>
        ) : null}
        {texte ? (
          <TexteBilingue texte={texte} langue={langue} changerLangue={setLangue} />
        ) : (
          <p className={styles.erreur}>{t('auth.conditions.texteIndisponible')}</p>
        )}
        <Checkbox
          id="cgu-acceptation"
          checked={coche}
          disabled={!texte}
          onCheckedChange={(valeur) => setCocheePour(valeur === true ? version : null)}
          label={t('auth.conditions.case', { version: dateVersion })}
        />
        {erreur ? (
          <p className={styles.erreur} role="alert">
            {erreur}
          </p>
        ) : null}
        <div className={styles.actions}>
          <Button
            type="submit"
            className={styles.action}
            disabled={!coche || !texte || enCours !== null}
            loading={enCours === 'accepter'}
          >
            {t('auth.conditions.accepter')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className={styles.action}
            disabled={enCours !== null}
            loading={enCours === 'refuser'}
            onClick={() => void refuser()}
          >
            {t('auth.conditions.refuser')}
          </Button>
        </div>
      </form>
    </AuthCard>
  );
}
